import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { HeaderBrandingValues } from '@cdf/api-client-react';
import { appIdentity } from '@/app/app-config';
import { useCustomerSkin, type CustomerLogo } from './customer-brand-provider';
import { CustomerLogoMark } from './customer-logo-mark';
import { TenantContextIndicator } from './tenant-context-indicator';

export const defaultHeaderOrder = ['applicationLogo', 'applicationName', 'entityLogo', 'entityTitle'] as const;
export type IdentityKey = typeof defaultHeaderOrder[number];
export const identityLabels: Record<IdentityKey, string> = {
  applicationLogo: 'Application logo', applicationName: 'Application name',
  entityLogo: 'Entity / customer logo', entityTitle: 'Entity title / context'
};

function SafeLogo({ logo }: { logo: Exclude<CustomerLogo, null> }) {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  if (failedSource === logo.dataUrl) return null;
  return <img src={logo.dataUrl} alt={logo.altText} onError={() => setFailedSource(logo.dataUrl)}
    className="max-h-8 max-w-20 shrink-0 object-contain sm:max-w-28" />;
}

export function IdentityPreview({ branding, applicationLogo, entityLogo, entityTitle, showEntityName = true }: {
  branding: HeaderBrandingValues; applicationLogo: CustomerLogo; entityLogo: CustomerLogo; entityTitle: React.ReactNode; showEntityName?: boolean;
}) {
  return <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1" data-header-identity="">
    {(branding.order ?? defaultHeaderOrder).filter(key => key !== 'entityTitle' || showEntityName).map(key => <span key={key} data-identity={key} className="min-w-0 max-w-full">
      {key === 'applicationLogo' ? <SafeLogo logo={applicationLogo ?? { dataUrl: appIdentity.iconPath, altText: '' }} /> :
        key === 'applicationName' ? <span title={branding.applicationName ?? appIdentity.name} className="block max-w-44 truncate text-sm font-semibold sm:max-w-60">{branding.applicationName ?? appIdentity.name}</span> :
        key === 'entityLogo' ? entityLogo && <SafeLogo logo={entityLogo} /> : entityTitle}
    </span>)}
  </div>;
}

export function HeaderIdentity() {
  const { headerBranding, logo, skin } = useCustomerSkin();
  const branding: HeaderBrandingValues = headerBranding?.effective ?? {};
  return <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1" data-header-identity="">
    {(branding.order ?? defaultHeaderOrder).map(key => {
      if (key === 'entityLogo') return logo ? <span key={key} data-identity={key} className="shrink-0"><CustomerLogoMark variant="inline" /></span> : null;
      if (key === 'entityTitle' && skin?.logo.showEntityName === false) return null;
      if (key === 'entityTitle') return <span key={key} data-identity={key} className="min-w-0"><TenantContextIndicator showLogo={false} /></span>;
      return <Link key={key} data-identity={key} to="/" aria-label={`${branding.applicationName ?? appIdentity.name} home`}
        className="focus-visible:ring-ring min-w-0 rounded-md focus-visible:ring-2 focus-visible:outline-none">
        {key === 'applicationLogo' ? <SafeLogo logo={headerBranding?.applicationLogo ?? { dataUrl: appIdentity.iconPath, altText: '' }} /> :
          <span title={branding.applicationName ?? appIdentity.name} className="block max-w-36 truncate text-sm font-semibold sm:max-w-56">{branding.applicationName ?? appIdentity.name}</span>}
      </Link>;
    })}
  </div>;
}
